/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import config from '@src/config'
import type {
  FetchWebhookEventsRequest,
  WebhookEventsResponse,
  WebhookEventsResultObject,
  WebhookTestEventResponse,
} from '@src/service'
import type { CodableWebhook } from '@src/service/model/WebhookModel'

import type Transport from '.'
import { HttpTransaction } from './transaction'

type FetchQuery = {
  limit: number
  dir: 'desc' | 'asc'
  page?: number
  eventTypes?: string
  statuses?: string
}

export type WebhookCreateInput = Pick<CodableWebhook, 'url' | 'triggers'> &
  Partial<Pick<CodableWebhook, 'label' | 'setting'>>

// The integration service validates these bodies and rejects null for optional fields.
function omitNullish<T extends object>(source: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== null && value !== undefined),
  ) as Partial<T>
}

export default class WebhooksClient {
  constructor(private transport: Transport) {}

  create({ url, triggers, label, setting }: WebhookCreateInput): Promise<CodableWebhook> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}webhooks`,
        body: omitNullish({ url, triggers, label, setting }),
      }),
    )
  }
  update({ id, ...webhook }: CodableWebhook): Promise<CodableWebhook> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.INTEGRATION_SERVICE_URL}webhooks/${id}`,
        body: omitNullish({
          label: webhook.label,
          url: webhook.url,
          triggers: webhook.triggers,
          key: webhook.key,
          setting: webhook.setting,
        }),
        headers: { 'If-Match': webhook.etag },
      }),
    )
  }

  delete(webhook: CodableWebhook) {
    return this.transport.queue(
      new HttpTransaction({
        method: 'delete',
        url: `${config.INTEGRATION_SERVICE_URL}webhooks/${webhook.id}`,
        headers: { 'If-Match': webhook.etag },
      }),
    )
  }

  fetch(): Promise<CodableWebhook[]> {
    return this.transport.queue(
      new HttpTransaction({
        url: `${config.INTEGRATION_SERVICE_URL}webhooks`,
      }),
    )
  }

  sendTestRequest(
    id: string,
    body: Record<string, any>,
  ): Promise<WebhookTestEventResponse> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}webhooks/${id}/events`,
        body: body,
      }),
    )
  }

  resendEvent(webhookId: string, eventId: string): Promise<WebhookEventsResultObject> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.INTEGRATION_SERVICE_URL}webhooks/${webhookId}/events/${eventId}`,
      }),
    )
  }

  fetchWebhookEvents({
    id,
    params,
  }: FetchWebhookEventsRequest): Promise<WebhookEventsResponse> {
    const { eventTypes, statuses, ...rest } = params ?? {}

    const query: FetchQuery = { limit: 10, dir: 'desc', ...rest }

    if (eventTypes?.length) {
      query.eventTypes = eventTypes.join(',')
    }

    if (statuses?.length) {
      query.statuses = statuses.join(',')
    }

    return this.transport.queue(
      new HttpTransaction({
        url: `${config.INTEGRATION_SERVICE_URL}webhooks/${id}/events`,
        query,
      }),
    )
  }
}
